import React from "react";

import "./Blog.css";
import NavLight from "../../components/NavLight";

import BlogPageIntroImg from "../../assets/blog/user_interviews_as_a_product_manager.png";
import BlogsPreview from "../../components/BlogsPreview";
import FoundersGuide from "../../components/FoundersGuide";
import {CustomMetaHelmet} from "../../components/CustomMetaHelmet";
import {meta} from "../../assets/meta/meta";

function HowToConductUserFeedbackInterview() {
    return (
        <div className="Blog1">
            <CustomMetaHelmet meta={meta["how-to-conduct-user-interviews-as-a-product-manager"]}/>
            <NavLight/>
            <section className="blog-page">
                <article className="blog-page-container">
                    <div>
                        <h1>How to conduct user interviews as a product manager</h1>
                        <p>
                            <i>
                                Being able to conduct user interviews is one of the single most important skills for
                                product managers (PMs).
                                When it’s done correctly it provides you with the insights you need to build products
                                that users love.
                                When it is done improperly it can waste both your valuable time and resources.
                                <p>&nbsp;</p>
                                In this guide to conducting user interviews for PMs I will share some of the best
                                practices I have accumulated from 10 years of talking to users and building products.
                                If you follow these best practices you are guaranteed to get better feedback out of your
                                user interviews.
                            </i>
                        </p>
                    </div>
                    <div>
                        <figure>
                            <img
                                src={BlogPageIntroImg}
                                alt="purple hand pressing a digital button"
                            />
                        </figure>
                    </div>
                    <div>
                        <h2>Preparing for user interviews</h2>
                        <p>
                            Preparation is the key to success with user interviews.
                            Prioritise time to review the users' LinkedIn, their behaviour in your product, and take
                            note of any feedback or communication they might have exchanged with you or your team prior
                            to the meeting.
                            If you use Magic Profiles, you can look up any user and see all their historical feedback
                            across all channels in your companies.
                            Most importantly, you should never come into a meeting with blind spots that you could have
                            avoided with a bit of diligent research.
                            <p>&nbsp;</p>
                            E.g. During a user interview the user might tell you that they like your product and find it
                            super useful.
                            However, because you have prepared you know they only logged in twice and haven’t been
                            active for weeks.
                            This creates an opportunity for you to dig into why the user is expressing that your app is
                            useful but hasn't used it for weeks.
                            Remember - people always love to please and be polite and nice, so try to dig beneath the
                            surface and find out evidence that supports their compliments.
                        </p>
                    </div>
                    <div>
                        <h2>How to start the user interview</h2>
                        <p>
                            While it’s tempting to just dive right into the interview it’s important to create a
                            connection and build trust upfront.
                            Make sure you have prepared a short and concise introduction of yourself and always
                            volunteer to introduce yourself.
                            After introductions you should spend a brief moment explaining how you will conduct the
                            interview.
                            Last, but not least, you should emphasise that the users shouldn’t hold off any criticism to
                            be nice.
                            <p>&nbsp;</p>
                            E.g. When introducing yourself keep in mind that the user will most likely observe and mimic
                            your introduction.
                            Therefore, make sure you introduce yourself with information that you would like to learn
                            about the user as well.
                            When emphasising that you embrace criticism you should make yourself vulnerable to create
                            psychological safety.
                            You can do that by expressing that the new feature/product you are trying to find out more
                            about has just been launched and is far from perfect. Always lead with honesty.
                        </p>
                    </div>
                    <div>
                        <h2>Body language and non-verbal cues</h2>
                        <p>
                            Conducting a user interview requires your full attention.
                            You have to carefully listen to what is being said while being observant of the user's body
                            language.
                            Make sure you take a few minutes before the user interview to clear your mind, close
                            distracting tabs, and mute notifications.
                            <p>&nbsp;</p>
                            E.g. During a user interview the user might tell you that the user experience was fine.
                            However, you noticed that the user squeezed their lips and hesitated to answer for a brief
                            moment.
                            There might be an opportunity for you to dig into what made the user hesitate and uncover
                            something they might have stopped themselves from saying out of kindness.
                        </p>
                    </div>
                    <div>
                        <h2>Focus on the user’s experience</h2>
                        <p>
                            As you start conducting many user interviews you will notice that user’s often move the
                            conversation away from their own user experience to giving you advice on how to improve your
                            product.
                            This is a common trap that will prevent you from capturing valuable feedback.
                            As soon as the user starts moving the conversation away from their own experience you should
                            ask questions to bring them back to their own user experience again.
                            <p>&nbsp;</p>
                            E.g. During a user interview the user might move away from their own user experience and
                            start suggesting that you implement gamification methods from popular apps.
                            This is your cue that the user is moving away from their own experience.
                            Instead bring the conversation back by exploring what made them suggest this implementation.
                            There might be an opportunity for you to dig into retention of your app based on their user
                            experience.
                        </p>
                    </div>
                    <div>
                        <h2>How to ask user interview questions</h2>
                        <p>
                            You can tell that you are great at asking user interview questions if the user is doing the
                            majority of the talking.
                            Your job is to get the other person to talk in order for you to learn new things about your
                            product.
                            As a rule of thumb you should aim to speak less than 20% of the time during the call.
                            When asking user interview questions make sure that they are open-ended. Ask questions that
                            begin with what, how, why.
                            <p>&nbsp;</p>
                            E.g. If you just launched a new product feature you can ask questions such as, what
                            motivated them to try your product?
                            What was the main benefit they got from using your product? What do you need to improve to
                            better meet the users needs?
                            You can also ask a combination of closed and open questions such as if they recommended your
                            product to someone else, and if they did, how did they describe your product when they did?
                        </p>
                    </div>
                    <div>
                        <h2>Asking great follow up questions</h2>
                        <p>
                            Mastering the art of asking great follow up questions in user interviews is a game-changer
                            when it comes to capturing invaluable user feedback.
                            I call it an art because it requires you to catch something interesting that’s worth
                            following up on and craft an impactful question in the moment to zero in on the specific
                            thing you want the user to elaborate on.
                            The key to becoming great at asking follow up questions is to give your undivided attention
                            to the user’s verbal and non-verbal communication and practise asking short and concise
                            questions with laser focus on what specifically you want the user to elaborate on.
                            <p>&nbsp;</p>
                            E.g. During a user interview the user might be talking fast and passionately about their
                            experience with your application.
                            For a brief moment they might mention that they probably should use it more often and then
                            quickly move on to something else.
                            In this particular moment you need to decide if you want to interrupt the conversation flow
                            of the user to dig deeper into what is preventing them from using your app more often.
                            Subsequently, you need to craft a short and concise follow up question to uncover what
                            prevents the user from using your app more often.
                        </p>
                    </div>
                    <div>
                        <h2>Respectfully challenge the user</h2>
                        <p>
                            Most users are going to answer in favour of being kind rather than honest.
                            This comes from a good place. People want to be helpful in a kind way.
                            However, this means that many users will stop themselves from giving you the feedback you
                            actually need to hear.
                            Therefore, it is important that you learn to respectfully challenge user’s to get the truth.
                            <p>&nbsp;</p>
                            E.g. During a user interview a user might tell you that they've been too busy to use the
                            product this week and that you probably know what that feels like.
                            Don’t assume you know what that feels like. This could be an opportunity for you to learn
                            about the daily routine of your ideal customer profile.
                            You could ask questions such as: Can you tell me more about your typical daily routine and
                            how you typically spend your time?
                            How does this product fit into your daily routine when you are using it?
                        </p>
                    </div>
                    <div>
                        <h2>Take good really good user interview notes</h2>
                        <p>
                            Taking good notes from your user interviews is a must.
                            Your notes will not only help you remember what is said, it will also spark inspiration and
                            motivation when you share them with your team.
                            Lastly, by taking notes and storing them you will be able to revisit them later when you
                            want to analyse all your notes for patterns.
                            If you use Magic Feedback, you can automatically unify and analyze all of your notes to
                            uncover insights, patterns, and trends.
                            <p>&nbsp;</p>
                            E.g. Once you have conducted many user interviews you will be able to analyse them for
                            patterns. This will help you understand the most popular requests by users, issues, and
                            examples of validation that can help you hone in on your value proposition.
                            If you use Magic Feedback, all of that can be extracted from your notes automatically using
                            artificial intelligence.
                            Schedule a free demo to learn more about Magic Feedback.
                        </p>
                    </div>
                </article>
            </section>
            <section className="related-blogs">
                <h1> You might also like</h1>
                <BlogsPreview/>
            </section>
            <section className="FoundersGuide">
                <FoundersGuide/>
            </section>
        </div>
    );
}

export default HowToConductUserFeedbackInterview;
